import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { organizationService } from "../../../services/api/organization";
import { documentService } from "../../../services/api/documentService";
import { toast } from "sonner";
import Button2 from "../../../components/elements/forms/Button2";
import MSAContent from "./MSAContent";
import StatusModal from "../../../components/Modal/StatusModal";
import PdfPreviewModal from "../../../components/Modal/PdfPreviewModal";
import { format } from "date-fns";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const MasterServiceAgreementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const contentRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientName, setClientName] = useState("[Customer Name]");
  const [representativeName, setRepresentativeName] = useState(
    "[Representative Name]",
  );
  const [representativeRole, setRepresentativeRole] = useState(
    "[Representative Role]",
  );
  const [agreed, setAgreed] = useState(false);
  const [uploadedSignature, setUploadedSignature] = useState<File | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean;
    status: "success" | "error";
    message: string;
  }>({
    isOpen: false,
    status: "success",
    message: "",
  });
  const [previewModal, setPreviewModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const today = format(new Date(), "do MMMM, yyyy");

  useEffect(() => {
    const fetchOrg = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const response = await organizationService.get(id);
        if (response?.data?.name) {
          setClientName(response.data.name);
        }
        if (response?.data?.representativeName) {
          setRepresentativeName(response.data.representativeName);
        }
        if (response?.data?.representativeRole) {
          setRepresentativeRole(response.data.representativeRole);
        }
      } catch (error) {
        console.error("Failed to fetch organization details", error);
        toast.error("Could not load organization details.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrg();
  }, [id]);

  const handleFileUpload = (file: File) => {
    setUploadedSignature(file);
    const previewUrl = URL.createObjectURL(file);
    setSignaturePreview(previewUrl);
  };

  const handleRemoveSignature = () => {
    if (signaturePreview) {
      URL.revokeObjectURL(signaturePreview);
    }
    setUploadedSignature(null);
    setSignaturePreview(null);
  };

  const generatePDF = async (): Promise<Blob | null> => {
    if (!contentRef.current) return null;
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.75);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        position,
        imgWidth,
        imgHeight,
        undefined,
        "FAST",
      );
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
        heightLeft -= pageHeight;
      }
      return pdf.output("blob");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF document");
      return null;
    }
  };

  const handlePreview = async () => {
    if (!agreed) {
      toast.error("Please agree to the Master Services Agreement to continue.");
      return;
    }
    if (!uploadedSignature) {
      toast.error("Please upload your signature before submitting.");
      return;
    }
    if (!id) {
      toast.error("Organization ID is missing.");
      return;
    }

    setPreviewModal(true);
    setPdfBlobUrl(null);
    setPdfFile(null);

    try {
      const pdfBlob = await generatePDF();
      if (!pdfBlob) {
        setPreviewModal(false);
        return;
      }
      const file = new File(
        [pdfBlob],
        `MSA_${clientName}_${format(new Date(), "yyyy-MM-dd")}.pdf`,
        { type: "application/pdf" },
      );
      setPdfFile(file);
      setPdfBlobUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      toast.error("Failed to generate PDF preview.");
      setPreviewModal(false);
    }
  };

  const handleSubmit = async () => {
    if (!pdfFile || !id) return;
    setIsSubmitting(true);
    try {
      await documentService.uploadDocument(id, "MSA", pdfFile);

      setPreviewModal(false);
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
      setPdfFile(null);

      setStatusModal({
        isOpen: true,
        status: "success",
        message: "Master Services Agreement submitted!",
      });
    } catch (error: any) {
      console.error("Error submitting MSA:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit Master Services Agreement";
      setStatusModal({
        isOpen: true,
        status: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePreview = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPdfFile(null);
    setPreviewModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading agreement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-[1024px] mx-auto">
        <h1 className="text-3xl font-bold text-center mb-4 text-gray-800">
          Review Master Services Agreement
        </h1>

        <div ref={contentRef}>
          <MSAContent
            clientName={clientName}
            representativeName={representativeName}
            representativeRole={representativeRole}
            date={today}
            effectiveDate={today}
            uploadedSignature={uploadedSignature}
            signaturePreview={signaturePreview}
            onFileUpload={handleFileUpload}
            onRemoveSignature={handleRemoveSignature}
          />
        </div>

        {/* Sticky Footer */}
        <div className="mt-4 bg-white p-4 rounded-lg shadow-sm border border-gray-100 sticky bottom-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="agree-checkbox"
              className="w-5 h-5 cursor-pointer accent-primary"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <label
              htmlFor="agree-checkbox"
              className="text-gray-700 cursor-pointer select-none text-sm"
            >
              I have read and agree to the Master Services Agreement
              {clientName !== "[Customer Name]" ? ` for ${clientName}` : ""}
            </label>
          </div>

          <div className="flex justify-end gap-4">
            {/* <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 font-medium transition-colors"
            >
              Back
            </button> */}
            <Button2
              title="Preview MSA"
              onSubmitHandler={handlePreview}
              className={`px-8 py-2 rounded-md text-white font-medium transition-colors ${
                agreed && uploadedSignature
                  ? "bg-primary hover:bg-primary/90"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
              disabled={!agreed || !uploadedSignature}
            />
          </div>
        </div>
      </div>

      <PdfPreviewModal
        isOpen={previewModal}
        onClose={handleClosePreview}
        pdfUrl={pdfBlobUrl}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        title="Master Services Agreement Preview"
        submitButtonText="Submit MSA"
      />

      <StatusModal
        isOpen={statusModal.isOpen}
        status={statusModal.status}
        message={statusModal.message}
        onClose={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
        onAction={
          statusModal.status === "success"
            ? () =>
                navigate({
                  pathname: `/sla`,
                  search: `Id=${id}`,
                })
            : undefined
        }
      />
    </div>
  );
};

export default MasterServiceAgreementPage;
